<template>
  <div class="fts-dashboard">
    <div class="d-actions" v-if="!builder">
      <fts-btn 
        outline 
        text-transform-init 
        :loading="isSchemaDownloading" 
        @click="downloadSchema" 
        v-if="isInternal"
      >
        <font-awesome-icon :icon="['fas', 'download']" /> Schema &nbsp;<fts-release-badge alpha />
      </fts-btn>
      <fts-btn outline text-transform-init @click="initDashboard" :disabled="loading">
        <font-awesome-icon :icon="['fas', 'sync-alt']" /> Refresh
      </fts-btn>
    </div>
    <div class="d-container">
      <div class="d-wrapper" v-for="x in design" :key="x.id">
        <template v-for="w in x.widgets">
          <fts-dashboard-widget :widget="w" :dashboard-data="dashboardData" :loading="loading" :key="w.id"
            @widget:click="onClick" />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { store as canvasDesignStore } from "../../store/canvasDesignStore";
import { authorizationStore } from "../../store/authorizationStore";
import commons from "../../commons/commons";

import FTSBtn from "../../elements/FTSBtn";
import FTSDashboardWidget from "../../dashboard-widget/FTSDashboardWidget";

export default {
  props: {
    builder: Boolean
  },
  components: {
    "fts-btn": FTSBtn,
    "fts-dashboard-widget": FTSDashboardWidget
  },
  data: () => ({
    dashboardData: {},
    loading: false,
    isSchemaDownloading: false,
    design: [
      {
        widgets: [
          {
            id: 'workspaceStream',
            name: 'Activity Stream',
            fieldName: 'workspaceStream',
            template: 'activityStream',
            source: 'studio',
          },
        ],
      },
      {
        widgets: [
          {
            id: 'workspaceHistory',
            name: 'Workspace History',
            fieldName: 'workspaceHistory',
            template: 'workspaceHistory',
          },
        ],
      },
      {
        widgets: [
          {
            id: 'workspaceSummary',
            fieldName: 'workspaceSummary',
            columns: [
              {
                id: 'currentUsers',
                name: 'Current users',
                fieldName: 'currentUsers',
                color: '#e8f5e9',
              },
              {
                id: 'activitiesDoneToday',
                name: 'Activities done this month',
                fieldName: 'activitiesDoneToday',
                color: '#fff8e1',
              },
              {
                id: 'creditUsedToday',
                name: 'Credit used this month',
                fieldName: 'creditUsedToday',
                color: '#e1f5fe',
              },
              {
                id: 'storageUsed',
                name: 'Storage used',
                fieldName: 'storageUsed',
                color: '#f3e5f5',
              },
            ],
            template: 'workspaceSummary'
          }
        ],
      }
    ],
  }),
  methods: {
    async onClick(item) {
      if (item.widgetId === 'workspaceHistory' && item.moduleId) {
        if (this.builder) {
          this.$emit("module:select", item.moduleId)
        } else {
          await canvasDesignStore.dispatch("loadVector", item.moduleId);
          this.$router.push({
            query: { id: commons.encodeids(item.moduleId) },
          });
        }
      } else if (item.widgetId === 'workspaceStream' && item.id) {
        const moduleId = item.moduleId || item.vectorId;
        window.open(`/#/dashboard?id=${commons.queryEncodeids(moduleId, item.id)}`, '_blank');
      } else {
        this.$toasted.global.error();
      }
    },
    async downloadSchema() {
      this.isSchemaDownloading = true;

      try {
        let response = await canvasDesignStore.dispatch('downloadSchema');
        // Create a blob link to download
        const blob = new Blob([response.data], { type: response.headers["content-type"] });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);

        // Use filename from API if provided
        var filename = `${canvasDesignStore.getters.workspaceName}.yaml`;

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(link.href); // cleanup
      } catch(err) {
        this.$toasted.global.error(err);
      } finally {
        this.isSchemaDownloading = false;
      }
    },
    async initDashboard() {
      this.loading = true
      this.dashboardData = {}

      try {
        let res = await canvasDesignStore.dispatch('retrieveDashboard');
        this.dashboardData = res.data;
        this.dashboardData = {
          workspaceStream: res.data.stream,
          workspaceSummary: {
            currentUsers: res.data.userCount,
            activitiesDoneToday: res.data.activityCount,
            creditUsedToday: res.data.activityCount,
            storageUsed: res.data.usage ? res.data.usage.storageUsed : null
          },
          workspaceHistory: res.data.history && res.data.history.list,
        };
      } catch (err) {
        if (err.response && err.response.data && err.response.data.message)
          console.error(err.response.data.message)
        else
          console.error(err);
      } finally {
        this.loading = false;
      }
    }
  },
  computed: {
    workspaceId() {
      return canvasDesignStore.getters.workspaceId;
    },
    workspaceModules() {
      return canvasDesignStore.getters.workspaceModules;
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
  },
  watch: {
    workspaceId() {
      this.initDashboard();
    },
  },
  mounted() {
    this.initDashboard();
  },
}
</script>

<style lang="scss">
$d-min-width: 350px;

.fts-dashboard {
  min-width: $d-min-width;

  .d-actions {
    padding: 0 1rem;
    text-align: right;
  }

  .d-container {
    display: flex;
    flex-wrap: wrap;
    padding: 0 1rem;

    .d-wrapper {
      min-width: 0;
      flex: 1 1 #{$d-min-width};
    }
  }
}
</style>